<template>
  <div class="case-ledger p-4">
    <div class="flex justify-between items-center mb-4">
      <h3 class="text-lg font-semibold">Loan Ledger & Repayment History</h3>
      <v-btn small color="primary" outlined @click="fetchEmiDetails" :loading="loading">
        <v-icon left small>mdi-refresh</v-icon>
        Refresh Ledger
      </v-btn>
    </div>

    <div v-if="loading && !ledgerData.length" class="text-center py-12">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-2 text-sm text-gray-500">Fetching ledger data...</div>
    </div>

    <div v-else-if="!ledgerData.length" class="text-center py-8">
      <v-icon size="64" color="grey lighten-1">mdi-book-open-variant</v-icon>
      <div class="mt-4 text-gray-600">No ledger entries available for this loan.</div>
    </div>

    <div v-else>
      <!-- Ledger Summary Stats -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div class="p-4 bg-white border-l-4 border-blue-500 shadow-sm rounded-r-lg">
          <div class="text-xs font-bold text-gray-500 uppercase mb-1">Sanctioned</div>
          <div class="text-xl font-bold text-blue-700">₹{{ formatCurrency(totalPrincipal) }}</div>
        </div>
        <div class="p-4 bg-white border-l-4 border-green-500 shadow-sm rounded-r-lg">
          <div class="text-xs font-bold text-gray-500 uppercase mb-1">Total Paid</div>
          <div class="text-xl font-bold text-green-700">₹{{ formatCurrency(totalPaid) }}</div>
        </div>
        <div class="p-4 bg-white border-l-4 border-red-500 shadow-sm rounded-r-lg">
          <div class="text-xs font-bold text-gray-500 uppercase mb-1">Balance Due</div>
          <div class="text-xl font-bold text-red-700">₹{{ formatCurrency(totalBalance) }}</div>
        </div>
        <div class="p-4 bg-white border-l-4 border-orange-500 shadow-sm rounded-r-lg">
          <div class="text-xs font-bold text-gray-500 uppercase mb-1">Total Late Fees</div>
          <div class="text-xl font-bold text-orange-700">₹{{ formatCurrency(totalLateFees) }}</div>
        </div>
      </div>

      <v-card outlined class="mb-4 overflow-hidden">
        <v-card-title
          class="subtitle-2 font-weight-bold py-2 bg-gray-50 flex justify-between items-center"
        >
          <span>Transactions for Loan: {{ selectedCase.caseNo }}</span>
          <v-chip x-small color="blue" label text-color="white"
            >{{ ledgerData.length }} Entries</v-chip
          >
        </v-card-title>

        <v-simple-table fixed-header height="450px" class="ledger-table min-w-full">
          <template v-slot:default>
            <thead>
              <tr class="bg-gray-100">
                <th class="text-left text-xs font-bold uppercase py-3 px-4" style="width: 50px">
                  No.
                </th>
                <th
                  class="text-left text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 120px"
                >
                  Date
                </th>
                <th
                  class="text-left text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 180px"
                >
                  Description
                </th>
                <th
                  class="text-right text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 110px"
                >
                  Principal
                </th>
                <th
                  class="text-right text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 110px"
                >
                  Interest
                </th>
                <th
                  class="text-right text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 140px"
                >
                  Late Fee / Penalty
                </th>
                <th
                  class="text-right text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 110px"
                >
                  Total Due
                </th>
                <th
                  class="text-right text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 110px"
                >
                  Paid Amount
                </th>
                <th
                  class="text-left text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 100px"
                >
                  Status
                </th>
                <th
                  class="text-center text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 120px"
                >
                  Overdue Days
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(entry, i) in flattenedLedgerEntries"
                :key="entry._id || i"
                :class="getRowClass(entry.emi, entry.type)"
              >
                <td class="text-sm border-b px-4">{{ i + 1 }}</td>
                <td class="text-sm border-b px-4 whitespace-nowrap">
                  <div class="font-semibold">{{ formatDate(entry.date) }}</div>
                </td>
                <td class="text-sm border-b px-4">
                  <div class="font-medium whitespace-nowrap">{{ entry.description }}</div>
                  <div class="text-xs text-gray-500 truncate max-w-[150px]">
                    {{ entry.method || (entry.type === 'installment' ? 'Installment' : '') }}
                  </div>
                </td>
                <td class="text-right text-sm border-b px-4 tabular-nums whitespace-nowrap">
                  <span v-if="entry.principal > 0">₹{{ formatCurrency(entry.principal) }}</span>
                  <span v-else class="text-gray-300">-</span>
                </td>
                <td class="text-right text-sm border-b px-4 tabular-nums whitespace-nowrap">
                  <span v-if="entry.interest > 0">₹{{ formatCurrency(entry.interest) }}</span>
                  <span v-else class="text-gray-300">-</span>
                </td>
                <td class="text-right text-sm border-b px-4 tabular-nums whitespace-nowrap">
                  <div v-if="entry.lateFee > 0" class="text-red-600 font-bold">
                    ₹{{ formatCurrency(entry.lateFee) }}
                  </div>
                  <div v-if="entry.penalty > 0" class="text-xs text-red-500">
                    + Penalty: ₹{{ formatCurrency(entry.penalty) }}
                  </div>
                  <div v-if="entry.lateFee <= 0 && entry.penalty <= 0" class="text-gray-300">-</div>
                </td>
                <td
                  class="text-right text-sm font-bold border-b px-4 tabular-nums whitespace-nowrap"
                >
                  <span v-if="entry.due > 0">₹{{ formatCurrency(entry.due) }}</span>
                  <span v-else class="text-gray-300">-</span>
                </td>
                <td
                  class="text-right text-sm text-green-700 font-bold border-b px-4 tabular-nums whitespace-nowrap"
                >
                  <span v-if="entry.paid > 0">₹{{ formatCurrency(entry.paid) }}</span>
                  <span v-else class="text-gray-300">-</span>
                </td>
                <td class="text-sm border-b px-4">
                  <v-chip
                    x-small
                    label
                    outlined
                    class="font-bold whitespace-nowrap"
                    :color="getStatusColor(entry.status)"
                  >
                    {{ entry.status }}
                  </v-chip>
                </td>
                <td class="text-center border-b px-4">
                  <div
                    v-if="
                      entry.type === 'installment' &&
                      (getOverdueInfo(entry.emi).isOverdue || getOverdueInfo(entry.emi).isLatePaid)
                    "
                    class="flex flex-col items-center"
                  >
                    <span
                      :class="
                        getOverdueInfo(entry.emi).isLatePaid ? 'text-orange-600' : 'text-red-600'
                      "
                      class="font-bold text-xs whitespace-nowrap"
                    >
                      {{ getOverdueInfo(entry.emi).days }} Days
                    </span>
                    <span class="text-[10px] uppercase text-gray-500 whitespace-nowrap">
                      {{ getOverdueInfo(entry.emi).isLatePaid ? 'Late' : 'Overdue' }}
                    </span>
                  </div>
                  <span v-else class="text-gray-300">-</span>
                </td>
              </tr>
            </tbody>
            <!-- Footer Summary -->
            <tfoot class="bg-gray-50 border-t-2">
              <tr class="font-bold">
                <td colspan="3" class="text-right py-3 px-4 whitespace-nowrap">
                  TOTAL REPAYMENT SUMMARY
                </td>
                <td class="text-right py-3 px-4 tabular-nums whitespace-nowrap">
                  ₹{{ formatCurrency(totalPrincipal) }}
                </td>
                <td class="text-right py-3 px-4 tabular-nums whitespace-nowrap">
                  ₹{{ formatCurrency(totalInterest) }}
                </td>
                <td class="text-right py-3 px-4 text-red-600 tabular-nums whitespace-nowrap">
                  ₹{{ formatCurrency(totalLateFees) }}
                </td>
                <td class="text-right py-3 px-4 tabular-nums whitespace-nowrap">
                  ₹{{ formatCurrency(totalDue) }}
                </td>
                <td class="text-right py-3 px-4 text-green-700 tabular-nums whitespace-nowrap">
                  ₹{{ formatCurrency(totalPaid) }}
                </td>
                <td colspan="2" class="bg-blue-50 text-center py-3 px-4">
                  <span class="text-xs text-blue-700 uppercase block whitespace-nowrap"
                    >Outs. Balance</span
                  >
                  <span class="text-blue-800 tabular-nums whitespace-nowrap"
                    >₹{{ formatCurrency(totalBalance) }}</span
                  >
                </td>
              </tr>
            </tfoot>
          </template>
        </v-simple-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import { calculateOverdueDays } from '@/utils/common'

export default {
  name: 'CaseLedger',
  props: {
    selectedCase: {
      type: Object,
      required: true
    },
    emiSchedule: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      apiData: [],
      useApiData: false
    }
  },
  computed: {
    ledgerData() {
      const data = this.useApiData ? this.apiData : this.emiSchedule
      return (data || []).filter((emi) => String(emi.emiStatus || '').toLowerCase() !== 'pending')
    },
    totalPrincipal() {
      return this.ledgerData.reduce((sum, emi) => sum + (parseFloat(emi.principalAmount) || 0), 0)
    },
    totalInterest() {
      return this.ledgerData.reduce((sum, emi) => sum + (parseFloat(emi.interestAmount) || 0), 0)
    },
    totalLateFees() {
      return this.ledgerData.reduce((sum, emi) => {
        const overdue = this.getOverdueInfo(emi).lateFeeAmount || 0
        const penalty = parseFloat(emi.penaltyAmount) || 0
        return sum + overdue + penalty
      }, 0)
    },
    totalDue() {
      const emiDue = this.ledgerData.reduce((sum, emi) => sum + this.calculateTotalDue(emi), 0)
      return emiDue + this.totalVasCharges
    },
    totalPaid() {
      return this.ledgerData.reduce((sum, emi) => {
        let paid = 0
        if (emi.emiPayments && emi.emiPayments.length) {
          paid = emi.emiPayments
            .filter((p) => !p.isDeleted)
            .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0)
        } else {
          paid = parseFloat(emi.paidAmount) || (this.isPaid(emi) ? parseFloat(emi.emiAmount) : 0)
        }
        return sum + paid
      }, 0)
    },
    totalVasCharges() {
      const additionalCharges = this.selectedCase?.loanDetails?.additionalCharges || []
      return additionalCharges.reduce((sum, charge) => {
        const netAmount =
          (parseFloat(charge.amount) || 0) +
          (parseFloat(charge.metaData?.gstAmount) || 0) -
          (parseFloat(charge.metaData?.discountAmount) || 0) -
          (parseFloat(charge.metaData?.waiverAmount) || 0)
        return sum + netAmount
      }, 0)
    },
    totalBalance() {
      return Math.max(0, this.totalDue - this.totalPaid)
    },
    flattenedLedgerEntries() {
      const entries = []
      const data = this.useApiData ? this.apiData : this.emiSchedule

      const filteredData = (data || []).filter(
        (emi) => String(emi.emiStatus || '').toLowerCase() !== 'pending'
      )

      filteredData.forEach((emi, index) => {
        // 1. Installment Due Entry
        entries.push({
          _id: emi._id + '_due',
          date: emi.dueDate,
          description: `EMI #${index + 1} Installment`,
          principal: parseFloat(emi.principalAmount) || 0,
          interest: parseFloat(emi.interestAmount) || 0,
          lateFee: this.getOverdueInfo(emi).lateFeeAmount || 0,
          penalty: parseFloat(emi.penaltyAmount) || 0,
          due: this.calculateTotalDue(emi),
          paid: 0,
          status: emi.emiStatus,
          type: 'installment',
          emiNo: index + 1,
          emi: emi
        })

        // 2. Payment Entries
        if (emi.emiPayments && emi.emiPayments.length) {
          const activePayments = emi.emiPayments.filter((p) => !p.isDeleted)
          activePayments.forEach((payment, pIndex) => {
            entries.push({
              _id: (payment._id || emi._id) + '_pay_' + pIndex,
              date: payment.date,
              description: `Payment for EMI #${index + 1}`,
              principal: 0,
              interest: 0,
              lateFee: 0,
              penalty: 0,
              due: 0,
              paid: parseFloat(payment.amount) || 0,
              status: emi.status || emi.emiStatus,
              type: 'payment',
              method: payment.paymentMode || 'Payment',
              emiNo: index + 1,
              emi: emi
            })
          })
        } else if (emi.emiStatus?.toLowerCase() === 'paid' || parseFloat(emi.paidAmount) > 0) {
          entries.push({
            _id: emi._id + '_pay_legacy',
            date: emi.paymentDate || emi.paidDate || emi.dueDate,
            description: `EMI Receipt #${index + 1}`,
            principal: 0,
            interest: 0,
            lateFee: 0,
            penalty: 0,
            due: 0,
            paid: parseFloat(emi.paidAmount) || parseFloat(emi.emiAmount) || 0,
            status: emi.emiStatus,
            type: 'payment',
            method: emi.paymentMethod || 'Legacy Payment',
            emiNo: index + 1,
            emi: emi
          })
        }
      })

      // 3. Additional Charges (VAS)
      const additionalCharges = this.selectedCase?.loanDetails?.additionalCharges || []
      additionalCharges.forEach((charge, cIndex) => {
        const netAmount =
          (parseFloat(charge.amount) || 0) +
          (parseFloat(charge.metaData?.gstAmount) || 0) -
          (parseFloat(charge.metaData?.discountAmount) || 0) -
          (parseFloat(charge.metaData?.waiverAmount) || 0)

        entries.push({
          _id: charge._id || 'vas_' + cIndex,
          date: charge.dueDate || this.selectedCase.loanDetails?.sanctionDate,
          description: charge.name || charge.feeType || 'Additional Charge',
          principal: parseFloat(charge.amount) || 0,
          interest: 0,
          lateFee: 0,
          penalty: 0,
          due: netAmount,
          paid: 0,
          status: charge.status || 'Due',
          type: 'charge',
          method: charge.remarks || 'VAS/Additional Charge',
          emiNo: '-',
          emi: null
        })
      })

      return entries.sort((a, b) => {
        const dateA = new Date(a.date)
        const dateB = new Date(b.date)
        if (dateA.getTime() !== dateB.getTime()) return dateA - dateB

        // Priority order: installment (1), charge (2), payment (3)
        const priority = { installment: 1, charge: 2, payment: 3 }
        return (priority[a.type] || 4) - (priority[b.type] || 4)
      })
    }
  },
  methods: {
    async fetchEmiDetails() {
      if (!this.selectedCase?._id) return

      this.loading = true
      try {
        const params = {
          limit: 1000,
          customer: this.selectedCase.customer?._id
        }

        if (this.selectedCase.loanDetails?._id) {
          params.loan = this.selectedCase.loanDetails._id
        }

        const response = await this.$axios.get('/collection/emi-details', { params })

        if (response.data && response.data.emiDetails) {
          this.apiData = response.data.emiDetails.filter(
            (emi) =>
              emi.loan?.applicationNumber === this.selectedCase.caseNo ||
              emi.case?._id === this.selectedCase._id
          )
          this.useApiData = true
          this.$toast.success('Ledger updated with latest data')
        }
      } catch (error) {
        console.error('Error fetching EMI details for ledger:', error)
        this.$toast.error('Failed to fetch latest EMI details from API')
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      return date ? this.$dayjs(date).format('DD MMM YYYY') : '-'
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0)
    },
    getOverdueInfo(emi) {
      return calculateOverdueDays(
        emi.dueDate,
        emi.emiStatus,
        emi.paymentDate,
        emi.emiAmount,
        emi.lateFeeType,
        emi.lateFeeValue
      )
    },
    calculateTotalDue(emi) {
      const emiAmount = parseFloat(emi.emiAmount) || 0
      const lateFee = this.getOverdueInfo(emi).lateFeeAmount || 0
      const penalty = parseFloat(emi.penaltyAmount) || 0
      return emiAmount + lateFee + penalty
    },
    isPaid(emi) {
      const status = String(emi.emiStatus || '').toLowerCase()
      return status === 'paid'
    },
    getStatusColor(status) {
      const s = String(status || '').toLowerCase()
      if (s === 'paid') return 'green'
      if (s === 'overdue') return 'red'
      if (s === 'partial paid' || s === 'minimum amount paid') return 'orange'
      return 'blue'
    },
    getRowClass(emi, type) {
      if (!emi) return ''
      if (type === 'payment') return 'bg-blue-50/20'
      const s = String(emi.emiStatus || '').toLowerCase()
      if (s === 'overdue') return 'bg-red-50/50'
      if (s === 'paid') return 'bg-green-50/10'
      return ''
    }
  }
}
</script>

<style scoped>
.ledger-table thead th {
  background-color: #f8f9fa !important;
  color: #37474f !important;
  border-bottom: 2px solid #cfd8dc !important;
  white-space: nowrap;
}

.ledger-table tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.ledger-table td {
  height: 60px !important;
  vertical-align: middle;
}

.tabular-nums {
  font-variant-numeric: tabular-nums;
}

/* Custom scrollbar for table */
.v-simple-table__wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.v-simple-table__wrapper::-webkit-scrollbar-thumb {
  background: #cfd8dc;
  border-radius: 10px;
}
.v-simple-table__wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
}

/* Sticky Footer */
.ledger-table :deep(.v-data-table__wrapper) {
  overflow-x: auto;
}

.ledger-table :deep(table) {
  border-collapse: separate;
  border-spacing: 0;
  padding: 0 !important;
}

.ledger-table :deep(tfoot) {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background-color: #f8f9fa !important;
}

.ledger-table :deep(tfoot tr td) {
  border-top: 2px solid #cfd8dc !important;
  background-color: #f8f9fa !important;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
}

/* Ensure blue balance cell maintains its background when sticky */
.ledger-table :deep(tfoot tr td.bg-blue-50) {
  background-color: #eff6ff !important;
}
</style>
